<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div class="d-flex align-items-center gap-3">
                <router-link
                  :to="{ name: 'Remissions' }"
                  class="btn btn-outline-secondary btn-sm"
                >
                  <i class="fas fa-arrow-left me-1"></i> Volver
                </router-link>
                <h6 class="mb-0">Picking / Packing — Remisión #{{ remissionId }}</h6>
              </div>
              <div class="d-flex gap-2">
                <button
                  v-if="!isOperator"
                  type="button"
                  class="btn btn-success btn-sm"
                  @click="showPickingModal = true"
                >
                  <i class="fas fa-box me-1"></i> Iniciar Picking / Packing
                </button>
                <button
                  v-if="!isOperator && canGenerateGuide"
                  type="button"
                  class="btn btn-sm"
                  style="background-color: #f747a1; border-color: #f747a1; color: #fff;"
                  :disabled="generatingGuide"
                  @click="openGuideModal"
                >
                  <i class="fas fa-shipping-fast me-1"></i> Generar guía
                </button>
                <button
                  v-if="!isOperator && hasWaybill"
                  type="button"
                  class="btn btn-sm"
                  style="background-color: #2dce89; border-color: #2dce89; color: #fff;"
                  :disabled="downloadingLabel"
                  @click="printLabel"
                >
                  <i class="fas" :class="downloadingLabel ? 'fa-spinner fa-spin' : 'fa-print'"></i> Imprimir rótulo
                </button>
              </div>
            </div>

            <div v-if="loading" class="text-center py-5">
              <i class="fas fa-spinner fa-spin fa-2x text-success"></i>
              <p class="mt-2 text-sm text-secondary">Cargando detalles...</p>
            </div>

            <div v-else-if="remission" class="mb-3">
              <div class="row">
                <div class="col-md-3">
                  <label class="form-control-label text-xs">Cliente</label>
                  <p class="text-sm font-weight-bold mb-0">{{ remission.name }}</p>
                </div>
                <div class="col-md-3">
                  <label class="form-control-label text-xs">Teléfono</label>
                  <p class="text-sm mb-0">{{ remission.phone }}</p>
                </div>
                <div class="col-md-3">
                  <label class="form-control-label text-xs">Estado</label><br>
                  <span class="badge badge-sm" :class="remission.type === 'return' ? 'bg-danger' : (remission.status == 1 ? 'bg-success' : 'bg-warning')">
                    {{ remission.type === 'return' ? 'Devolución' : (remission.status == 1 ? 'Formalizada' : 'Pendiente') }}
                  </span>
                  <span v-if="remission.preparado == 4" class="badge badge-sm bg-primary ms-1">
                    <i class="fas fa-hammer"></i> Fabricado
                  </span>
                  <span v-else-if="remission.preparado == 1" class="badge badge-sm bg-info text-dark ms-1">
                    <i class="fas fa-check-circle"></i> Preparado
                  </span>
                  <span v-else-if="remission.preparado == 2" class="badge badge-sm bg-success ms-1">
                    <i class="fas fa-check-double"></i> Terminado
                  </span>
                  <span v-else-if="remission.preparado == 5" class="badge badge-sm ms-1" style="background-color: #2e389b; color: #fff;">
                    <i class="fas fa-truck"></i> Despachado
                  </span>
                </div>
                <div class="col-md-3">
                  <label class="form-control-label text-xs">Fecha</label>
                  <p class="text-sm mb-0">{{ new Date(remission.created_at).toLocaleDateString('es-ES') }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="card-body px-0 pt-0 pb-2">
            <div v-if="!loading && details.length > 0" class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Prep.</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Disponible</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Producto</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Ref</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Talla</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Color</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Código de barras</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Cant / Stock</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Precio</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in details" :key="item.id">
                    <td class="text-center align-middle">
                      <i v-if="item.prepared" class="fas fa-check-circle text-success" title="Preparado"></i>
                      <i v-else class="fas fa-clock text-warning" title="No preparado"></i>
                    </td>
                    <td class="text-center align-middle">
                      <i v-if="item.prepared" class="fas fa-check-circle text-success" title="Preparado"></i>
                      <template v-else-if="isProductionOrder || itemStock(item) < item.quantity">
                        <span v-if="itemManufacturingStatus(item) === 'completed'" class="status-badge status-badge--completed">
                          <i class="fas fa-check-circle"></i> Fabricado
                        </span>
                        <select
                          v-else
                          :value="itemManufacturingStatus(item)"
                          class="status-select"
                          :class="statusPillClass(item)"
                          @change="onStatusChange($event, item)"
                        >
                          <option value="pending">Pendiente</option>
                          <option value="in_progress">En proceso</option>
                          <option value="completed">Fabricado</option>
                        </select>
                      </template>
                      <i v-else class="fas fa-check-circle text-success" title="Stock suficiente"></i>
                    </td>
                    <td class="align-middle">
                      <h6 class="mb-0 text-sm">{{ item.inventory?.name || 'Producto' }}</h6>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs text-secondary">{{ item.inventory?.ref || '—' }}</span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs text-secondary">{{ itemSize(item) }}</span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs text-secondary">{{ itemColor(item) }}</span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs text-secondary">{{ itemBarcode(item) }}</span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-sm font-weight-bold">{{ item.quantity }}</span> / 
                      <span class="badge badge-sm" :class="itemStock(item) <= 0 ? 'bg-danger' : (itemStock(item) < item.quantity ? 'bg-warning text-dark' : 'bg-success')">
                        {{ itemStock(item) }}
                      </span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs text-secondary">{{ parseFloat(item.unit_price || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs font-weight-bold">{{ parseFloat(item.total || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div class="px-4 py-3 d-flex gap-2">
                <span class="badge bg-secondary">Pendientes: {{ statusCount('pending') }}</span>
                <span class="badge bg-info text-dark">En proceso: {{ statusCount('in_progress') }}</span>
                <span class="badge bg-success">Fabricados: {{ statusCount('completed') }}</span>
              </div>
            </div>

            <div v-else-if="!loading" class="text-center py-4">
              <p class="text-secondary text-sm">No hay detalles para esta remisión.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Picking / Packing -->
    <div v-if="showPickingModal" class="picking-modal-overlay" @click.self="showPickingModal = false">
      <div class="picking-modal-box card shadow">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0">Escanear producto — Remisión #{{ remissionId }}</h6>
            <button type="button" class="btn-close" @click="showPickingModal = false"></button>
          </div>

          <div class="picking-summary mb-3">
            <span class="badge bg-success">Preparados: {{ preparedCount }}</span>
            <span class="badge bg-warning text-dark">No preparados: {{ details.length - preparedCount }}</span>
          </div>

          <!-- Barra de progreso -->
          <div class="picking-progress mb-3">
            <div class="picking-progress-bar" :style="{ width: progressPercent + '%' }"></div>
          </div>
          <div class="text-center text-xs text-secondary mb-3">
            {{ preparedCount }} / {{ details.length }} ({{ progressPercent }}%)
          </div>

          <label class="form-control-label text-sm">Escanear código de barras</label>
          <div class="d-flex gap-2 mb-3">
            <input
              ref="barcodeInput"
              v-model="barcodeScan"
              type="text"
              class="form-control form-control-sm"
              placeholder="Escanee o digite el código de barras..."
              @input="onBarcodeInput"
              @keyup.enter="onBarcodeScan"
            />
            <button type="button" class="btn btn-success btn-sm d-flex align-items-center justify-content-center" @click="onBarcodeScan" :disabled="!barcodeScan.trim()" style="margin-bottom: 0; flex-shrink: 0;">
              <i class="fas fa-check"></i>
            </button>
          </div>
          <div v-if="scanMessage" class="mt-2 mb-3" :class="scanError ? 'text-danger' : 'text-success'">
            <small>{{ scanMessage }}</small>
          </div>

          <div class="picking-items-list">
            <div
              v-for="item in sortedDetails"
              :key="item.id"
              class="picking-item-row"
              :class="{ 'picking-item-row--prepared': item.prepared }"
            >
              <div class="picking-item-status">
                <i v-if="item.prepared" class="fas fa-check-circle text-success"></i>
                <i v-else class="fas fa-clock text-warning"></i>
              </div>
              <div class="picking-item-info">
                <div class="picking-item-name">{{ item.inventory?.name || 'Producto' }}</div>
                <div class="picking-item-meta">
                  Ref: {{ item.inventory?.ref || '—' }} ·
                  Talla: {{ itemSize(item) }} ·
                  Color: {{ itemColor(item) }} ·
                  Cant: {{ item.quantity }} ·
                  Barcode: {{ itemBarcode(item) }}
                </div>
              </div>
              <div v-if="!item.prepared" class="picking-item-actions">
                <button
                  type="button"
                  class="btn btn-outline-success btn-sm mb-0"
                  title="Marcar como preparado manualmente"
                  :disabled="manualMarkingId === item.id"
                  @click="markPreparedManual(item)"
                >
                  <i class="fas" :class="manualMarkingId === item.id ? 'fa-spinner fa-spin' : 'fa-check'"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-end mt-3">
            <button type="button" class="btn btn-secondary btn-sm" @click="showPickingModal = false">
              <i class="fas fa-times me-1"></i> Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Generar guía de envío -->
    <div v-if="showGuideModal" class="picking-modal-overlay" @click.self="showGuideModal = false">
      <div class="guide-modal-box card shadow">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0">Generar guía de envío — Remisión #{{ remissionId }}</h6>
            <button type="button" class="btn-close" @click="showGuideModal = false"></button>
          </div>

          <div class="guide-columns">
            <!-- Card izquierdo: información de ubicación -->
            <div class="guide-card">
              <p class="guide-card-title">Ubicación del envío</p>

              <label class="guide-label">Origen</label>
              <div class="guide-info-box mb-3">
                <p class="mb-1"><strong>{{ guideOrigin.company }}</strong></p>
                <p class="mb-1 text-sm">{{ guideOrigin.street }}</p>
                <p class="mb-0 text-sm text-secondary">
                  {{ guideOrigin.cityLabel }} · DANE {{ guideOrigin.city }}
                </p>
              </div>

              <label class="guide-label">Destinatario (datos de la venta)</label>
              <div class="guide-info-box">
                <p class="mb-1"><strong>{{ receiverInfo.name }}</strong></p>
                <p class="mb-1 text-sm" v-if="receiverInfo.document">{{ receiverInfo.document_type }} {{ receiverInfo.document }}</p>
                <p class="mb-1 text-sm">{{ receiverInfo.phone || 'Sin teléfono' }}</p>
                <p class="mb-1 text-sm">{{ receiverInfo.address || 'Sin dirección' }}</p>
                <p class="mb-0 text-sm text-secondary">
                  {{ receiverInfo.city || 'Sin ciudad' }}<template v-if="receiverInfo.province">, {{ receiverInfo.province }}</template>
                  <template v-if="receiverDane"> · DANE {{ receiverDane }}</template>
                </p>
              </div>
            </div>

            <!-- Card derecho: datos a enviar a ConEnvios (editables) -->
            <div class="guide-card">
              <p class="guide-card-title">Datos para ConEnvios</p>

              <div class="row">
                <div class="col-6 mb-2">
                  <label class="guide-label">Nombre destinatario</label>
                  <input v-model="guideForm.receiver_name" type="text" class="form-control form-control-sm" />
                </div>
                <div class="col-3 mb-2">
                  <label class="guide-label">Tipo doc.</label>
                  <select v-model="guideForm.receiver_document_type" class="form-select form-select-sm">
                    <option>CC</option>
                    <option>CE</option>
                    <option>TI</option>
                    <option>NIT</option>
                  </select>
                </div>
                <div class="col-3 mb-2">
                  <label class="guide-label">Documento</label>
                  <input v-model="guideForm.receiver_document" type="text" class="form-control form-control-sm" />
                </div>
                <div class="col-6 mb-2">
                  <label class="guide-label">Teléfono</label>
                  <input v-model="guideForm.receiver_phone" type="text" class="form-control form-control-sm" />
                </div>
                <div class="col-6 mb-2">
                  <label class="guide-label">Email</label>
                  <input v-model="guideForm.receiver_email" type="email" class="form-control form-control-sm" />
                </div>
                <div class="col-12 mb-2">
                  <label class="guide-label">Dirección</label>
                  <input v-model="guideForm.receiver_street" type="text" class="form-control form-control-sm" />
                </div>
                <div class="col-6 mb-2">
                  <label class="guide-label">Ciudad (DANE)</label>
                  <input :value="receiverDane || 'No encontrado'" type="text" class="form-control form-control-sm" disabled />
                </div>
                <div class="col-6 mb-2">
                  <label class="guide-label">Departamento</label>
                  <input :value="receiverInfo.province || '—'" type="text" class="form-control form-control-sm" disabled />
                </div>
                <div class="col-4 mb-2">
                  <label class="guide-label">Peso (kg)</label>
                  <input v-model.number="guideForm.package_weight" type="number" min="0.1" step="0.1" class="form-control form-control-sm" />
                </div>
                <div class="col-8 mb-2">
                  <label class="guide-label">Contenido del paquete</label>
                  <input v-model="guideForm.package_description" type="text" class="form-control form-control-sm" />
                </div>
                <div class="col-6 mb-2">
                  <label class="guide-label">Método de pago</label>
                  <select v-model="guideForm.payment_method" class="form-select form-select-sm">
                    <option value="">Flete pago</option>
                    <option value="Contraentrega">Contraentrega</option>
                    <option value="Flete Contraentrega">Flete Contraentrega</option>
                  </select>
                </div>
                <div class="col-6 mb-2 d-flex align-items-end">
                  <div class="form-check">
                    <input v-model="guideForm.chargeShipping" class="form-check-input" type="checkbox" id="chargeShippingCheck" />
                    <label class="form-check-label text-sm" for="chargeShippingCheck">Cobrar envío al cliente</label>
                  </div>
                </div>
                <div class="col-12 mb-2">
                  <label class="guide-label">Notas</label>
                  <input v-model="guideForm.notes" type="text" class="form-control form-control-sm" />
                </div>
              </div>
            </div>
          </div>

          <!-- Transportadoras -->
          <div class="guide-carriers mt-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <p class="guide-card-title mb-0">Transportadoras</p>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                :disabled="calculatingShipping"
                @click="calculateShipping"
              >
                <i class="fas" :class="calculatingShipping ? 'fa-spinner fa-spin' : 'fa-calculator'"></i>
                {{ shippingOptions.length ? 'Recalcular' : 'Calcular' }}
              </button>
            </div>

            <div v-if="calculatingShipping" class="text-center py-3">
              <i class="fas fa-spinner fa-spin fa-lg text-secondary"></i>
              <p class="text-sm text-secondary mt-2 mb-0">Calculando tarifas de envío...</p>
            </div>
            <div v-else-if="shippingCalcError" class="alert alert-warning py-2 px-3 mb-0 text-sm">
              <i class="fas fa-exclamation-triangle me-1"></i> {{ shippingCalcError }}
            </div>
            <div v-else-if="shippingOptions.length === 0" class="text-center py-3 text-sm text-secondary">
              Presiona "Calcular" para ver las transportadoras disponibles.
            </div>
            <div v-else class="guide-carrier-list">
              <button
                v-for="(opt, idx) in shippingOptions"
                :key="idx"
                type="button"
                class="guide-carrier-row"
                :class="{ 'guide-carrier-row--selected': selectedCarrierIdx === idx }"
                @click="selectedCarrierIdx = idx"
              >
                <img v-if="carrierImage(opt)" :src="carrierImage(opt)" alt="" class="guide-carrier-img" />
                <i v-else class="fas fa-truck guide-carrier-img-placeholder"></i>
                <span class="guide-carrier-name">{{ carrierName(opt) }}</span>
                <span class="guide-carrier-days">{{ deliveryLabel(opt) }}</span>
                <span class="guide-carrier-price">{{ formatCop(carrierPrice(opt)) }}</span>
              </button>
            </div>
          </div>

          <div class="d-flex justify-content-end gap-2 mt-3">
            <button type="button" class="btn btn-secondary btn-sm" @click="showGuideModal = false">Cancelar</button>
            <button
              type="button"
              class="btn btn-sm"
              style="background-color: #f747a1; border-color: #f747a1; color: #fff;"
              :disabled="generatingGuide || selectedCarrierIdx === null"
              @click="generateGuide"
            >
              <i class="fas" :class="generatingGuide ? 'fa-spinner fa-spin' : 'fa-file-export'"></i>
              Generar guía
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import remissionsService from '@/services/remissions.service';
import remissionsDetailsService from '@/services/remissionsDetails.service';
import conenviosService from '@/services/conenvios.service';
import ciudadesDane from '@/assets/data/ciudades.json';

export default {
  name: 'PickingPacking',
  data() {
    return {
      remissionId: null,
      remission: null,
      details: [],
      loading: true,
      showPickingModal: false,
      barcodeScan: '',
      scanMessage: '',
      scanError: false,
      manualMarkingId: null,
      // Generación de guía (ConEnvios)
      showGuideModal: false,
      calculatingShipping: false,
      generatingGuide: false,
      downloadingLabel: false,
      shippingOptions: [],
      selectedCarrierIdx: null,
      shippingCalcError: '',
      shippingConfig: null,
      guideForm: {},
    };
  },
  computed: {
    isOperator() {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      return user?.role === '3' || user?.role === 3;
    },
    guideOrigin() {
      const o = this.shippingConfig?.origin || {};
      return {
        company: this.shippingConfig?.company_name || 'Empresa',
        street: o.street || '—',
        city: o.city || '—',
        cityLabel: this.cityLabelFromDane(o.city),
        neighborhood: o.neighborhood || '',
        state: o.state || '',
        country: o.country || 'CO',
      };
    },
    receiverInfo() {
      const r = this.remission || {};
      const u = r.user || {};
      return {
        name: this.guideForm.receiver_name || u.name || r.name || '—',
        document_type: this.guideForm.receiver_document_type || r.document_type || 'CC',
        document: this.guideForm.receiver_document || r.document || u.document || '',
        phone: this.guideForm.receiver_phone || r.phone || u.phone || '',
        email: this.guideForm.receiver_email || r.email || u.email || '',
        address: this.guideForm.receiver_street || r.address || u.address || '',
        city: u.city || '',
        province: u.province || '',
      };
    },
    receiverDane() {
      return this.guideForm.receiver_dane || '';
    },
    hasWaybill() {
      return !!this.waybillId;
    },
    waybillId() {
      const j = this.remission?.conenvios_json;
      if (!j) return null;
      return j.id || j.data?.id || null;
    },
    canGenerateGuide() {
      // Todo preparado (preparado = 1) y sin guía generada
      return !!(this.remission && this.remission.preparado == 1 && !this.hasWaybill);
    },
    isProductionOrder() {
      // Pedidos de producción (is_production_order=1): no se valida stock en la
      // columna Disponible, siempre se muestra el estado para pasar a En proceso.
      // Las remisiones tipo venta (is_production_order=0) sí validan stock.
      return !!(this.remission?.is_production_order);
    },
    preparedCount() {
      return this.details.filter(d => d.prepared).length;
    },
    progressPercent() {
      if (this.details.length === 0) return 0;
      return Math.round((this.preparedCount / this.details.length) * 100);
    },
    sortedDetails() {
      return [...this.details].sort((a, b) => {
        if (a.prepared === b.prepared) return 0;
        return a.prepared ? 1 : -1;
      });
    },
  },
  methods: {
    itemManufacturingStatus(item) {
      return item.manufacturing_status || 'pending';
    },
    statusLabel(item) {
      const s = this.itemManufacturingStatus(item);
      if (s === 'completed') return 'Fabricado';
      if (s === 'in_progress') return 'En proceso';
      return 'Pendiente';
    },
    statusPillClass(item) {
      const s = this.itemManufacturingStatus(item);
      if (s === 'completed') return 'status-pill--completed';
      if (s === 'in_progress') return 'status-pill--in-progress';
      return 'status-pill--pending';
    },
    statusIconClass(item) {
      const s = this.itemManufacturingStatus(item);
      if (s === 'completed') return 'fas fa-check-circle';
      if (s === 'in_progress') return 'fas fa-cog fa-spin';
      return 'fas fa-clock';
    },
    statusCount(status) {
      return this.details.filter(d => this.itemManufacturingStatus(d) === status).length;
    },
    revertStatus(target, item, current) {
      target.value = current;
      item.manufacturing_status = current;
    },
    async onStatusChange(event, item) {
      const newStatus = event.target.value;
      const current = this.itemManufacturingStatus(item);
      const target = event.target;

      if (newStatus === current) return;

      if (newStatus === 'completed') {
        const result = await Swal.fire({
          title: 'Confirmar fabricado',
          text: `¿Cambiar el estado de "${item.inventory?.name || 'Producto'}" a Fabricado? Se sumarán ${item.quantity} unidades al inventario.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sí, marcar fabricado',
          cancelButtonText: 'Cancelar',
          customClass: {
            container: 'swal-high-z',
            popup: 'swal-status-popup',
            confirmButton: 'swal-status-confirm',
            cancelButton: 'swal-status-cancel',
          },
        });

        if (!result.isConfirmed) {
          this.revertStatus(target, item, current);
          return;
        }
      }

      try {
        const res = await remissionsDetailsService.updateManufacturingStatus(item.id, newStatus);
        const updated = res.data.detail;
        const idx = this.details.findIndex(d => d.id === item.id);
        if (idx >= 0) {
          this.details[idx] = {
            ...this.details[idx],
            manufacturing_status: updated.manufacturing_status,
            manufactured: updated.manufactured,
            inventory: updated.inventory,
            variant: updated.variant,
            available_stock: updated.available_stock,
          };
        }
        await Swal.fire({
          icon: 'success',
          title: 'Estado actualizado',
          text: res.data.message,
          timer: 1500,
          showConfirmButton: false,
          customClass: { container: 'swal-high-z' },
        });

        // Si el backend cambió el preparado de la remisión a En proceso (3)
        if (res.data.remission_preparado_changed) {
          this.remission = { ...this.remission, preparado: res.data.remission_preparado };
        }
      } catch (error) {
        console.error('Error al actualizar estado:', error);
        this.revertStatus(target, item, current);
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'No se pudo actualizar el estado',
          customClass: { container: 'swal-high-z' },
        });
      }
    },
    itemSize(item) {
      if (item.variant?.size?.size) return item.variant.size.size;
      if (item.productionModel?.size?.size) return item.productionModel.size.size;
      return '—';
    },
    itemColor(item) {
      if (item.variant?.color?.name) return item.variant.color.name;
      if (item.productionModel?.size?.color?.name) return item.productionModel.size.color.name;
      if (item.inventory?.color?.name) return item.inventory.color.name;
      return '—';
    },
    itemStock(item) {
      // available_stock ya descuenta las cantidades apartadas por pedidos Fabricados
      if (item.available_stock !== undefined && item.available_stock !== null) {
        return item.available_stock;
      }
      if (item.variant) {
        return item.variant.quantity ?? 0;
      }
      return item.inventory?.quantity ?? 0;
    },
    itemBarcode(item) {
      if (item.variant && item.variant.barcode) return item.variant.barcode;
      return item.inventory?.barcode || '—';
    },
    // ===== Guía de envío (ConEnvios) =====
    normalizeCity(val) {
      return String(val || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .replace(/\s+/g, ' ')
        .trim();
    },
    resolveDaneCode(city, province) {
      const target = this.normalizeCity(city);
      if (!target) return '';
      const prov = this.normalizeCity(province);

      const score = (c) => {
        const nombre = this.normalizeCity(c.nombre);
        const dpto = this.normalizeCity(c.dpto);
        let s = 0;
        if (nombre === target) s += 3;
        else if (nombre.startsWith(target) || target.startsWith(nombre)) s += 2;
        else if (nombre.split(' ')[0] === target.split(' ')[0]) s += 1;
        else return -1;
        if (prov && dpto === prov) s += 2;
        return s;
      };

      const candidates = ciudadesDane
        .filter((c) => c.activo !== false)
        .map((c) => ({ c, s: score(c) }))
        .filter((x) => x.s > 0)
        .sort((a, b) => b.s - a.s);

      return candidates.length ? candidates[0].c.codigo_dane : '';
    },
    cityLabelFromDane(dane) {
      const hit = ciudadesDane.find((c) => c.codigo_dane === String(dane || ''));
      return hit ? `${hit.nombre}, ${hit.dpto}` : dane || '—';
    },
    async loadShippingConfig() {
      if (this.shippingConfig) return this.shippingConfig;
      try {
        const res = await conenviosService.getConfig();
        this.shippingConfig = res.data?.data || null;
      } catch (e) {
        console.error('Error cargando config de envíos:', e);
        this.shippingConfig = null;
      }
      return this.shippingConfig;
    },
    async openGuideModal() {
      if (!this.remission) return;

      const config = (await this.loadShippingConfig()) || {};
      const restricted = (config.restricted_cities || []).map((c) => this.normalizeCity(c));

      const city = this.remission.user?.city || '';
      const province = this.remission.user?.province || '';

      // Validación previa: ciudades restringidas (entrega local)
      if (restricted.includes(this.normalizeCity(city))) {
        Swal.fire(
          'Entrega local',
          `El cliente está en ${city || 'una ciudad restringida'}. Esta ciudad no genera guía porque el despacho se realiza de forma local.`,
          'info'
        );
        return;
      }

      // Resolver código DANE del destinatario
      const dane = this.resolveDaneCode(city, province) || this.resolveDaneCode(province, '');
      if (!dane) {
        Swal.fire(
          'Ciudad no encontrada',
          `No se encontró el código DANE para "${city || province || '—'}". Agrega la ciudad al archivo ciudades.json para poder generar la guía.`,
          'warning'
        );
        return;
      }

      const r = this.remission;
      const u = r.user || {};
      this.guideForm = {
        receiver_name: u.name || r.name || '',
        receiver_document_type: r.document_type || 'CC',
        receiver_document: r.document || u.document || '',
        receiver_phone: r.phone || u.phone || '',
        receiver_email: r.email || u.email || '',
        receiver_street: r.address || u.address || '',
        receiver_dane: dane,
        package_description: this.details
          .map((d) => d.inventory?.name || '')
          .filter(Boolean)
          .join(', '),
        package_weight: 0.2,
        payment_method: '',
        chargeShipping: true,
        notes: `Remisión #${r.id}`,
      };

      this.shippingOptions = [];
      this.selectedCarrierIdx = null;
      this.shippingCalcError = '';
      this.showGuideModal = true;

      // Calcular automáticamente al abrir
      this.calculateShipping();
    },
    buildPackageDetail() {
      return [
        {
          type: 'Box',
          package_number: '',
          description: this.guideForm.package_description || 'Productos',
          weight: Number(this.guideForm.package_weight) || 0.2,
          quantity: 1,
          price: parseFloat(this.remission?.grand_total) || 0,
          dimensions: { length: 1, width: 1, height: 1 },
        },
      ];
    },
    async calculateShipping() {
      if (!this.remission || !this.guideForm.receiver_dane) return;
      this.calculatingShipping = true;
      this.shippingCalcError = '';
      this.shippingOptions = [];
      this.selectedCarrierIdx = null;
      try {
        const payload = {
          remission_id: this.remission.id,
          sender_city: this.guideOrigin.city,
          recipient_city: this.guideForm.receiver_dane,
          package_detail: this.buildPackageDetail(),
          charge_shipping_to_customer: !!this.guideForm.chargeShipping,
          payment_method: this.guideForm.payment_method || '',
          package_type: 'Paquetes',
          notes: this.guideForm.notes || '',
          external_id: `rem-${this.remission.id}`,
        };
        const res = await conenviosService.calculate(payload);
        const data = res.data?.data;
        this.shippingOptions = Array.isArray(data)
          ? data
          : Array.isArray(data?.options)
            ? data.options
            : Array.isArray(data?.data)
              ? data.data
              : [];
        if (this.shippingOptions.length === 0) {
          this.shippingCalcError = 'ConEnvios no devolvió transportadoras para esta ruta.';
        }
      } catch (e) {
        this.shippingCalcError = e?.response?.data?.message || 'No se pudieron calcular las tarifas de envío.';
      } finally {
        this.calculatingShipping = false;
      }
    },
    carrierName(opt) {
      return opt.carrier_name || opt.carrier?.name || opt.name || 'Transportadora';
    },
    carrierImage(opt) {
      return opt.carrier_image || opt.carrier?.image || opt.image || '';
    },
    carrierPrice(opt) {
      return parseFloat(opt.price ?? opt.total ?? opt.cost ?? 0) || 0;
    },
    deliveryLabel(opt) {
      const days = opt.delivery_days ?? opt.delivery_time ?? opt.eta;
      return days ? `${days}` : '—';
    },
    formatCop(val) {
      return '$ ' + (parseFloat(val) || 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    async generateGuide() {
      if (this.selectedCarrierIdx === null || !this.remission) return;
      const opt = this.shippingOptions[this.selectedCarrierIdx];
      if (!opt) return;

      const origin = this.guideOrigin;
      const dane = this.guideForm.receiver_dane;

      const payload = {
        remission_id: this.remission.id,
        sender: {
          address: {
            street: origin.street,
            city: origin.city,
            neighborhood: origin.neighborhood || '',
            state: origin.state || String(origin.city).substring(0, 2),
            country: origin.country || 'CO',
          },
        },
        receiver: {
          name: this.guideForm.receiver_name,
          address: {
            street: this.guideForm.receiver_street,
            city: dane,
            neighborhood: '',
            state: dane.substring(0, 2),
            country: 'CO',
          },
          email: this.guideForm.receiver_email || '',
          document_type: this.guideForm.receiver_document_type || 'CC',
          document_number: this.guideForm.receiver_document || '10000000',
          phone: this.guideForm.receiver_phone || '',
        },
        package_type: 'Paquetes',
        package_details: this.buildPackageDetail(),
        payment_method: this.guideForm.payment_method || '',
        status: 'Aceptado',
        charge_shipping_to_customer: !!this.guideForm.chargeShipping,
        notes: this.guideForm.notes || '',
        external_id: `rem-${this.remission.id}`,
        carrier_id: opt.carrier_id ?? opt.carrierId ?? '',
        shipping_method: opt.carrier_id ?? opt.carrierId ?? '',
        shipping_cost: this.carrierPrice(opt),
      };

      this.generatingGuide = true;
      try {
        const res = await conenviosService.generateWaybill(payload);
        const response = res.data?.data || {};
        this.remission.conenvios_json = response;
        this.showGuideModal = false;
        await Swal.fire('Guía generada', 'La guía de envío fue creada correctamente. Ya puedes imprimir el rótulo.', 'success');
      } catch (e) {
        Swal.fire('Error', e?.response?.data?.message || 'No se pudo generar la guía.', 'error');
      } finally {
        this.generatingGuide = false;
      }
    },
    async printLabel() {
      const id = this.waybillId;
      if (!id) return;
      this.downloadingLabel = true;
      try {
        const res = await conenviosService.getWaybillPdf([String(id)]);
        const base64 = res.data?.data;
        if (!base64) throw new Error('Respuesta vacía');
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], { type: 'application/pdf' });
        window.open(URL.createObjectURL(blob), '_blank');
      } catch (e) {
        Swal.fire('Error', e?.response?.data?.message || 'No se pudo obtener el rótulo en PDF.', 'error');
      } finally {
        this.downloadingLabel = false;
      }
    },
    async loadData() {
      this.remissionId = this.$route.query.id;
      if (!this.remissionId) {
        this.loading = false;
        return;
      }
      try {
        const [remRes, detRes] = await Promise.all([
          remissionsService.getData(this.remissionId),
          remissionsDetailsService.getData(this.remissionId),
        ]);
        this.remission = remRes.data;
        this.details = (detRes.data || []).map(d => ({
          ...d,
          prepared: !!d.prepared,
        }));
      } catch (error) {
        console.error('Error al cargar datos:', error);
        await Swal.fire('Error', 'No se pudieron cargar los datos de la remisión', 'error');
      } finally {
        this.loading = false;
      }
    },
    onBarcodeInput() {
      clearTimeout(this._barcodeTimer);
      const code = this.barcodeScan.trim();
      if (!code) return;
      this._barcodeTimer = setTimeout(() => {
        const match = this.details.find(d => {
          if (d.variant && d.variant.barcode) {
            return d.variant.barcode === code;
          }
          const inv = d.inventory;
          if (!inv) return false;
          if (inv.barcode === code) return true;
          const variants = inv.shopify_details?.variants || [];
          return variants.some(v => v.barcode === code);
        });
        if (match) {
          this.onBarcodeScan();
        }
      }, 300);
    },
    async onBarcodeScan() {
      const code = this.barcodeScan.trim();
      if (!code) return;
      this.scanMessage = '';
      this.scanError = false;
      try {
        const res = await remissionsService.markPrepared(this.remissionId, code);
        const detailId = res.data.detail?.id;
        if (detailId) {
          const item = this.details.find(d => d.id === detailId);
          if (item) item.prepared = true;
        }
        this.scanMessage = res.data.message || 'Producto marcado como preparado';

        if (res.data.remission_preparado) {
          this.remission = { ...this.remission, preparado: true };
          await Swal.fire({
            icon: 'success',
            title: '¡Pedido preparado!',
            text: 'Todos los productos han sido marcados como preparados.',
            timer: 2000,
            showConfirmButton: false,
            customClass: { container: 'swal-high-z' },
          });
        }

        this.barcodeScan = '';
        this.$nextTick(() => {
          if (this.$refs.barcodeInput) this.$refs.barcodeInput.focus();
        });
      } catch (error) {
        this.scanError = true;
        this.scanMessage = error.response?.data?.message || 'Error al marcar el producto';
        if (error.response?.status === 422) {
          await Swal.fire({
            icon: 'warning',
            title: 'Stock insuficiente',
            text: error.response?.data?.message || 'No hay stock suficiente',
            customClass: {
              container: 'swal-high-z',
            },
          });
        }
        this.barcodeScan = '';
        this.$nextTick(() => {
          if (this.$refs.barcodeInput) this.$refs.barcodeInput.focus();
        });
      }
    },
    async markPreparedManual(item) {
      this.manualMarkingId = item.id;
      this.scanMessage = '';
      this.scanError = false;
      try {
        const res = await remissionsService.markPreparedById(item.id);
        const idx = this.details.findIndex(d => d.id === item.id);
        if (idx >= 0) {
          this.details[idx].prepared = true;
        }
        this.scanMessage = res.data.message || 'Producto marcado como preparado';

        if (res.data.remission_preparado) {
          this.remission = { ...this.remission, preparado: true };
          await Swal.fire({
            icon: 'success',
            title: '¡Pedido preparado!',
            text: 'Todos los productos han sido marcados como preparados.',
            timer: 2000,
            showConfirmButton: false,
            customClass: { container: 'swal-high-z' },
          });
        }
      } catch (error) {
        this.scanError = true;
        this.scanMessage = error.response?.data?.message || 'Error al marcar el producto';
        if (error.response?.status === 422) {
          await Swal.fire({
            icon: 'warning',
            title: 'Stock insuficiente',
            text: error.response?.data?.message || 'No hay stock suficiente',
            customClass: { container: 'swal-high-z' },
          });
        }
      } finally {
        this.manualMarkingId = null;
      }
    },
  },
  watch: {
    showPickingModal(val) {
      if (val) {
        this.barcodeScan = '';
        this.scanMessage = '';
        this.scanError = false;
        this.$nextTick(() => {
          if (this.$refs.barcodeInput) this.$refs.barcodeInput.focus();
        });
      }
    },
  },
  async mounted() {
    await this.loadData();
  },
};
</script>

<style scoped>
.picking-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.picking-modal-box {
  width: 600px;
  max-width: 95vw;
  max-height: 85vh;
  overflow-y: auto;
}

.picking-summary {
  display: flex;
  gap: 0.5rem;
}

.picking-progress {
  width: 100%;
  height: 8px;
  background: #e9ecef;
  border-radius: 999px;
  overflow: hidden;
}

.picking-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #82d616, #4caf50);
  border-radius: 999px;
  transition: width 0.4s ease;
}

.picking-item-actions {
  flex-shrink: 0;
}

.picking-items-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.picking-item-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid #f1f1f1;
}

.picking-item-row:last-child {
  border-bottom: none;
}

.picking-item-row--prepared {
  background: #f0fff4;
}

.picking-item-status {
  font-size: 1.1rem;
  flex-shrink: 0;
}

.picking-item-info {
  flex: 1;
  min-width: 0;
}

.picking-item-name {
  font-weight: 600;
  font-size: 0.875rem;
}

.picking-item-meta {
  font-size: 0.75rem;
  color: #6c757d;
}

.status-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.3rem 1.8rem 0.3rem 0.8rem;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1.2;
  outline: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='currentColor' viewBox='0 0 16 16'%3e%3cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.55rem center;
  background-size: 10px;
}

.status-select:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.status-select:focus {
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.status-select option {
  font-size: 0.85rem;
  padding: 0.3rem 0.5rem;
  border-radius: 0;
}

.status-pill--pending {
  background: #f3f4f6;
  color: #6b7280;
}

.status-pill--pending:hover {
  background: #e5e7eb;
}

.status-pill--in-progress {
  background: #fef3c7;
  color: #b45309;
}

.status-pill--in-progress:hover {
  background: #fde68a;
}

.status-pill--completed {
  background: #d1fae5;
  color: #047857;
}

.status-pill--completed:hover {
  background: #a7f3d0;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 0.8; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(1.3); }
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.3rem 0.8rem;
  border-radius: 999px;
  white-space: nowrap;
  line-height: 1.2;
}

.status-badge--completed {
  background: #d1fae5;
  color: #047857;
}
</style>

<style>
.swal-high-z {
  z-index: 100000 !important;
}

.swal-status-popup {
  border-radius: 16px !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
}

.swal-status-select {
  border: 2px solid #e2e8f0 !important;
  border-radius: 10px !important;
  padding: 0.7rem 2.5rem 0.7rem 1rem !important;
  font-size: 0.95rem !important;
  font-weight: 600 !important;
  color: #334155 !important;
  background-color: #f8fafc !important;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%2364748b' viewBox='0 0 16 16'%3e%3cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3e%3c/svg%3e") !important;
  background-repeat: no-repeat !important;
  background-position: right 0.85rem center !important;
  background-size: 14px !important;
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
  margin-top: 0.75rem !important;
}

.swal-status-select:focus {
  border-color: #6366f1 !important;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.12) !important;
  outline: none !important;
  background-color: #fff !important;
}

.swal-status-select:hover {
  border-color: #cbd5e1 !important;
  background-color: #f1f5f9 !important;
}

.swal-status-confirm {
  background-color: #6366f1 !important;
  border: none !important;
  border-radius: 10px !important;
  font-weight: 600 !important;
  padding: 0.6rem 1.5rem !important;
  font-size: 0.9rem !important;
  transition: all 0.2s ease !important;
}

.swal-status-confirm:hover {
  background-color: #4f46e5 !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3) !important;
}

.swal-status-cancel {
  border-radius: 10px !important;
  font-weight: 600 !important;
  padding: 0.6rem 1.5rem !important;
  font-size: 0.9rem !important;
  transition: all 0.2s ease !important;
}

.swal-status-cancel:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

/* ===== Modal Generar guía (ConEnvios) ===== */
.guide-modal-box {
  width: 100%;
  max-width: 980px;
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 14px;
}
.guide-columns {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 1rem;
}
@media (max-width: 860px) {
  .guide-columns { grid-template-columns: 1fr; }
}
.guide-card {
  background: #f8f9fa;
  border: 1px solid rgba(18, 18, 18, 0.06);
  border-radius: 12px;
  padding: 0.9rem;
}
.guide-card-title {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(18, 18, 18, 0.55);
  margin: 0 0 0.6rem;
}
.guide-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(18, 18, 18, 0.55);
  margin-bottom: 0.2rem;
  display: block;
}
.guide-info-box {
  background: #fff;
  border: 1px solid rgba(18, 18, 18, 0.08);
  border-radius: 10px;
  padding: 0.7rem 0.8rem;
}
.guide-info-box p {
  font-size: 0.82rem;
}
.guide-info-box .text-sm {
  font-size: 0.8rem;
}
.guide-carrier-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  max-height: 240px;
  overflow-y: auto;
}
.guide-carrier-row {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  width: 100%;
  text-align: left;
  background: #fff;
  border: 1.5px solid rgba(18, 18, 18, 0.1);
  border-radius: 10px;
  padding: 0.55rem 0.8rem;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.guide-carrier-row:hover {
  border-color: #f747a1;
  background: #fff5fa;
}
.guide-carrier-row--selected {
  border-color: #f747a1 !important;
  background: #fdeef6 !important;
}
.guide-carrier-img {
  height: 26px;
  max-width: 90px;
  object-fit: contain;
  flex-shrink: 0;
}
.guide-carrier-img-placeholder {
  font-size: 1.1rem;
  color: rgba(18, 18, 18, 0.35);
  width: 90px;
  text-align: center;
}
.guide-carrier-name {
  flex: 1;
  font-size: 0.85rem;
  font-weight: 600;
  color: #121212;
}
.guide-carrier-days {
  font-size: 0.75rem;
  color: rgba(18, 18, 18, 0.55);
  white-space: nowrap;
}
.guide-carrier-price {
  font-size: 0.9rem;
  font-weight: 700;
  color: #121212;
  white-space: nowrap;
}
</style>
